import { MARJ_ERIME_PUAN } from './uyariEsikleri';
import { sayi, type SaKategoriSatiri, type Sayisal } from './ozetHesaplari';

/** 990054 Satış Analiz · Zararına Satış (990033 tanimi). */
export interface ZararSatiri {
  urun: string;
  kategori: string;
  satilan_miktar: Sayisal;
  ort_satis_fiyat: Sayisal;
  birim_maliyet: Sayisal;
  zarar: Sayisal;
}

/** 990055 Satış Analiz · İade Oranı (oran yuzde). */
export interface IadeOraniSatiri {
  lokasyon: string;
  iade: Sayisal;
  net_ciro: Sayisal;
  oran: Sayisal;
  onceki_iade: Sayisal;
  onceki_net_ciro: Sayisal;
  onceki_oran: Sayisal;
}

const yuvarla2 = (n: number) => Math.round(n * 100) / 100;

export function zararinaSatis(rows: ZararSatiri[]): { adet: number; toplam: number } | null {
  if (rows.length === 0) return null;
  return { adet: rows.length, toplam: rows.reduce((t, r) => t + sayi(r.zarar), 0) };
}

export interface MarjErimesi {
  ad: string;
  marj: number;
  oncekiMarj: number;
  fark: number;
}

/**
 * (kar/ciro − onceki_kar/onceki_ciro) × 100 ≤ −esik. Fark 2 haneye YUVARLANIP karsilastirilir: ekranda
 * "−3,00 puan" yazan bir satirin kayan nokta yuzunden (−2,9999…) gizlenmemesi icin.
 * Onceki ciro yoksa/0 ya da bu donem ciro 0 ise karsilastirilamaz (sifir varsayilmaz).
 */
export function marjErimeleri(rows: SaKategoriSatiri[], esik = MARJ_ERIME_PUAN): MarjErimesi[] {
  const sonuc: MarjErimesi[] = [];
  for (const r of rows) {
    if (r.tur !== 'kategori') continue;
    const ciro = sayi(r.ciro);
    const oncekiCiro = sayi(r.onceki_ciro);
    if (ciro === 0 || oncekiCiro <= 0) continue;
    const marj = (sayi(r.kar) / ciro) * 100;
    const oncekiMarj = (sayi(r.onceki_kar) / oncekiCiro) * 100;
    const fark = yuvarla2(marj - oncekiMarj);
    if (fark <= -esik) sonuc.push({ ad: String(r.ad), marj, oncekiMarj, fark });
  }
  return sonuc.sort((a, b) => a.fark - b.fark);
}

export interface IadeArtisi {
  oran: number;
  oncekiOran: number;
  subeler: Array<{ lokasyon: string; oran: number }>;
}

/**
 * Firma geneli oran sube oranlarinin ORTALAMASI DEGIL, tutarlardan: Σiade / Σnet_ciro. Yalniz artmissa doner.
 */
export function iadeArtisi(rows: IadeOraniSatiri[]): IadeArtisi | null {
  let iade = 0;
  let net = 0;
  let oncekiIade = 0;
  let oncekiNet = 0;
  for (const r of rows) {
    iade += sayi(r.iade);
    net += sayi(r.net_ciro);
    oncekiIade += sayi(r.onceki_iade);
    oncekiNet += sayi(r.onceki_net_ciro);
  }
  if (net <= 0 || oncekiNet <= 0) return null;
  const oran = (iade / net) * 100;
  const oncekiOran = (oncekiIade / oncekiNet) * 100;
  if (!(oran > oncekiOran)) return null;
  const subeler = rows
    .filter((r) => r.oran !== null && r.oran !== undefined)
    .map((r) => ({ lokasyon: String(r.lokasyon), oran: sayi(r.oran) }))
    .sort((a, b) => b.oran - a.oran);
  return { oran, oncekiOran, subeler };
}

export interface NegatifMarj {
  ad: string;
  kar: number;
  marj: number | null;
}

export function negatifMarjlilar(rows: SaKategoriSatiri[]): NegatifMarj[] {
  return rows
    .filter((r) => r.tur === 'kategori' && sayi(r.kar) < 0)
    .map((r) => {
      const ciro = sayi(r.ciro);
      const kar = sayi(r.kar);
      return { ad: String(r.ad), kar, marj: ciro !== 0 ? (kar / ciro) * 100 : null };
    })
    .sort((a, b) => a.kar - b.kar);
}
